import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, ArrowLeft, Mail, Calendar, Send, Edit, Save } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const AdminEmailAutomation = ({ user }) => {
  const navigate = useNavigate();
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editedSubject, setEditedSubject] = useState('');
  const [editedBody, setEditedBody] = useState('');

  const defaultTemplates = {
    welcome: { subject: 'Welcome to VowFlow! Your Journey Begins', body: `We're thrilled to have you join VowFlow!\n\nYour wedding planning journey starts here. We've built beautiful tools to help you organize every detail with ease and joy.\n\nTake a moment to explore your dashboard, chat with Grace our AI assistant, and start building your perfect day.\n\nWe're here to support you every step of the way.` },
    month_12: { subject: 'Just Engaged! Your VowFlow Journey Begins', body: `Congratulations on your engagement! This is such an exciting time.\n\nWith 12 months ahead, you have plenty of time to plan thoughtfully. This month, focus on:\n\n- Setting your wedding date\n- Discussing your vision and budget\n- Starting your guest list draft\n- Researching venues\n\nRemember: planning should be enjoyable, not stressful.` },
    month_11: { subject: '11 Months to Go - Building Your Vision', body: `You're 11 months out - the perfect time to solidify your wedding vision!\n\nThis month's focus:\n- Book your ceremony and reception venues\n- Start interviewing photographers\n- Consider your wedding party\n- Research caterers` },
    month_10: { subject: '10 Months Out - Booking Key Vendors', body: `At 10 months out, it's time to secure your dream team!\n\nPriorities this month:\n- Book your photographer and videographer\n- Reserve your caterer\n- Start dress/suit shopping\n- Consider hiring a day-of coordinator` },
    month_9: { subject: '9 Months to Go - Details Coming Together', body: `9 months until your celebration! Things are taking shape beautifully.\n\nThis month:\n- Finalize your wedding party\n- Book florist and DJ/band\n- Order your wedding dress/suit\n- Start planning your honeymoon` },
    month_8: { subject: '8 Months Out - Guest List & Save the Dates', body: `With 8 months to go, it's time to finalize your guest list!\n\nFocus areas:\n- Complete your guest list\n- Order and send Save the Dates\n- Book remaining vendors\n- Schedule dress fittings` },
    month_7: { subject: '7 Months to Go - Registry & Attire', body: `7 months out - you're well into the planning groove!\n\nThis month:\n- Create your wedding registry\n- Order bridesmaids/groomsmen attire\n- Book hair and makeup artists\n- Start planning rehearsal dinner` },
    month_6: { subject: 'Halfway There! 6 Months Until Your Big Day', body: `You've reached the halfway point! How exciting!\n\nPriorities now:\n- Order wedding invitations\n- Plan ceremony details and music\n- Book hotel room blocks\n- Finalize menu with caterer` },
    month_5: { subject: '5 Months Out - Invitations & Final Vendors', body: `5 months to go! Time is flying.\n\nKey tasks:\n- Send wedding invitations\n- Book any remaining vendors\n- Plan wedding day timeline\n- Schedule final dress fitting` },
    month_4: { subject: '4 Months to Go - RSVPs & Details', body: `4 months out - the excitement is building!\n\nFocus on:\n- Track RSVPs as they arrive\n- Finalize ceremony and reception details\n- Order wedding favors\n- Plan seating arrangements` },
    month_3: { subject: '3 Months Out - Final Details & Confirmations', body: `Just 3 months away! Time to finalize everything.\n\nCritical tasks:\n- Final walkthrough with venue\n- Confirm all vendor contracts\n- Create seating chart\n- Order programs and signage` },
    month_2: { subject: '2 Months to Go - Almost There!', body: `Two months until your wedding day!\n\nFinal priorities:\n- Final dress/suit fittings\n- Confirm final guest count\n- Break in your wedding shoes\n- Arrange transportation` },
    month_1: { subject: 'One Month Away - Your Wedding Month is Here!', body: `Your wedding is ONE MONTH AWAY! Can you believe it?\n\nFinal month checklist:\n- Final meeting with all vendors\n- Confirm timeline with wedding party\n- Pack for honeymoon\n- Get marriage license` },
    guest_save_60: { subject: 'Save the Date', body: `Dear [Guest Name],\n\nWe are delighted to announce our upcoming wedding and would be honored by your presence.\n\nPlease save the date. Formal invitation to follow.` },
    guest_rsvp_30: { subject: 'RSVP Reminder', body: `Dear [Guest Name],\n\nWe hope you received our wedding invitation! Please let us know if you can celebrate with us.` },
    guest_details_7: { subject: 'Final Wedding Details', body: `Dear [Guest Name],\n\nOur wedding is just days away! Here are the final details you need to know.` },
    guest_thanks_3: { subject: 'Thank You', body: `Dear [Guest Name],\n\nThank you so much for being part of our special day! Your presence meant the world to us.` }
  };

  const handleEdit = (automation) => {
    setSelectedEmail(automation);
    const template = defaultTemplates[automation.id] || { subject: '', body: '' };
    setEditedSubject(template.subject);
    setEditedBody(template.body);
    setEditing(true);
  };

  const handleSave = () => {
    toast.success('Email template updated!');
    setEditing(false);
    setSelectedEmail(null);
  };

  const emailAutomations = [
    { id: 'welcome', name: 'Welcome Email', trigger: 'On Signup', status: 'Active' },
    { id: 'month_12', name: '12 Months Before', trigger: 'When wedding date set', status: 'Active' },
    { id: 'month_11', name: '11 Months Before', trigger: 'Auto-scheduled', status: 'Active' },
    { id: 'month_10', name: '10 Months Before', trigger: 'Auto-scheduled', status: 'Active' },
    { id: 'month_9', name: '9 Months Before', trigger: 'Auto-scheduled', status: 'Active' },
    { id: 'month_8', name: '8 Months Before', trigger: 'Auto-scheduled', status: 'Active' },
    { id: 'month_7', name: '7 Months Before', trigger: 'Auto-scheduled', status: 'Active' },
    { id: 'month_6', name: '6 Months Before', trigger: 'Auto-scheduled', status: 'Active' },
    { id: 'month_5', name: '5 Months Before', trigger: 'Auto-scheduled', status: 'Active' },
    { id: 'month_4', name: '4 Months Before', trigger: 'Auto-scheduled', status: 'Active' },
    { id: 'month_3', name: '3 Months Before', trigger: 'Auto-scheduled', status: 'Active' },
    { id: 'month_2', name: '2 Months Before', trigger: 'Auto-scheduled', status: 'Active' },
    { id: 'month_1', name: '1 Month Before', trigger: 'Auto-scheduled', status: 'Active' },
    { id: 'guest_save_60', name: 'Guest: Save the Date', trigger: '60 days before (client approval)', status: 'Active' },
    { id: 'guest_rsvp_30', name: 'Guest: RSVP Reminder', trigger: '30 days before (client approval)', status: 'Active' },
    { id: 'guest_details_7', name: 'Guest: Final Details', trigger: '7 days before (client approval)', status: 'Active' },
    { id: 'guest_thanks_3', name: 'Guest: Thank You', trigger: '3 days after (client approval)', status: 'Active' },
  ];

  return (
    <div className="min-h-screen bg-[#0f0f0f]">
      <nav className="bg-[#1C1917] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin')} className="p-2 hover:bg-white/5 rounded-full transition-colors">
              <ArrowLeft size={20} className="text-white/60" />
            </button>
            <Heart className="text-[#881337]" size={28} fill="#881337" />
            <span className="text-xl font-serif font-bold text-white">VowFlow</span>
            <span className="text-xs bg-[#881337] text-white px-2 py-0.5 rounded-full ml-2">ADMIN</span>
          </div>
        </div>
      </nav>

      <div className="max-w-[1600px] mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-serif text-white mb-2">Email Automation</h1>
          <p className="text-white/50">Manage automated email campaigns and templates</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="bg-[#1C1917] rounded-2xl border border-white/10">
              <div className="p-6 border-b border-white/10">
                <h2 className="text-lg font-medium text-white">Automated Email Campaigns</h2>
                <p className="text-white/50 text-sm mt-1">All emails send automatically based on triggers</p>
              </div>
              
              <div className="divide-y divide-white/5">
                {emailAutomations.map((automation) => (
                  <div key={automation.id} className="p-4 hover:bg-white/5 transition-colors cursor-pointer" onClick={() => handleEdit(automation)}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Mail className="text-[#881337]" size={20} />
                        <div>
                          <p className="text-white font-medium">{automation.name}</p>
                          <p className="text-white/40 text-sm">{automation.trigger}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-xs">
                          {automation.status}
                        </span>
                        <Edit size={16} className="text-white/40" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-[#1C1917] rounded-2xl p-6 border border-white/10 sticky top-24">
              <h2 className="text-lg font-medium text-white mb-4">Automation Stats</h2>
              <div className="space-y-4">
                <div>
                  <p className="text-white/50 text-sm">Total Automations</p>
                  <p className="text-3xl font-serif text-white mt-1">{emailAutomations.length}</p>
                </div>
                <div>
                  <p className="text-white/50 text-sm">Active Campaigns</p>
                  <p className="text-3xl font-serif text-green-400 mt-1">{emailAutomations.filter(e => e.status === 'Active').length}</p>
                </div>
                <div className="pt-4 border-t border-white/10">
                  <p className="text-white/60 text-sm leading-relaxed">
                    Emails are sent automatically to clients based on their wedding date. Guest emails require client approval before sending.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {editing && selectedEmail && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#1C1917] rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden border border-white/10">
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <div>
                <h2 className="text-xl font-serif font-bold text-white">{selectedEmail.name}</h2>
                <p className="text-white/50 text-sm mt-1">Edit email template</p>
              </div>
              <button onClick={() => setEditing(false)} className="text-white/60 hover:text-white text-3xl">×</button>
            </div>
            
            <div className="overflow-y-auto p-6 max-h-[70vh] space-y-5">
              <div>
                <label className="text-white/60 text-sm mb-2 block">Subject Line</label>
                <input
                  type="text"
                  value={editedSubject}
                  onChange={(e) => setEditedSubject(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white placeholder:text-white/30"
                  placeholder="Email subject..."
                />
              </div>

              <div>
                <label className="text-white/60 text-sm mb-2 block">Email Body</label>
                <textarea
                  value={editedBody}
                  onChange={(e) => setEditedBody(e.target.value)}
                  rows={16}
                  className="w-full bg-white/5 border border-white/10 rounded-lg p-4 text-white placeholder:text-white/30 font-mono text-sm"
                  placeholder="Email body... Use [Couple Names] for personalization"
                />
              </div>

              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                <p className="text-amber-300 text-sm">
                  <strong>Tips:</strong> Use line breaks to separate paragraphs. Dates will be auto-highlighted. Include warm, supportive language.
                </p>
              </div>
            </div>
            
            <div className="flex gap-3 p-6 border-t border-white/10">
              <button
                onClick={() => setEditing(false)}
                className="flex-1 bg-white/5 hover:bg-white/10 text-white rounded-lg py-3 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="flex-1 bg-[#881337] hover:bg-[#9F1239] text-white rounded-lg py-3 flex items-center justify-center gap-2 transition-colors"
              >
                <Save size={18} />
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEmailAutomation;